import { ApiError } from 'api/api-error';
import { CatalogueType, SaleType } from 'api/query/catalogue';
import { CatalogueAnimal } from 'api/query/catalogue/catalogue-animal';
import { CatalogueExtended } from 'api/query/catalogue/catalogue-extended';
import { CatalogueItem } from 'api/query/catalogue/catalogue-item';
import { CataloguePayload } from 'api/query/catalogue/catalogue-payload';
import { DropdownItem } from 'components/dropdown/dropdown-item';
import moment from 'moment';
import { Params } from 'navi';
import { AustralianState } from 'shared/australian-state.enum';
import { getFilePayload } from 'shared/image-upload-utils';
import { NewZealandState } from 'shared/newzealand-state.enum';
import { Sex } from 'shared/sex.enum';
import { toQuerystring } from 'utils/navigation-utils';
import { asPlural } from 'utils/utils';
import XLSX from 'xlsx';
import { CatalogueFormField } from '.';
import { CatalogueRowAnimal } from './catalogue-row-animal';

/**
 * Gets an initial state for the catalogue.
 */
export const getCatalogueInitialState = (catalogue?: CataloguePayload) => {
  const cat: CataloguePayload = {
    address: '',
    catalogueImages: undefined,
    catalogueItems: undefined,
    contactEmail: '',
    contactName: '',
    contactPhoneNumber: '',
    contactUrl: '',
    country: 'Australia',
    dbType: 0,
    description: '',
    flock: '',
    id: 0,
    isActive: undefined,
    listingEndDate: undefined,
    listingStartDate: undefined,
    onRamSelect: undefined,
    postcode: undefined,
    propertyName: '',
    saleEndDate: undefined,
    saleStartDate: undefined,
    saleType: SaleType.SingleVendorAuction,
    state: '',
    city: '',
    title: '',
    userId: undefined,
    ...catalogue,
  };

  return cat;
};

/**
 * Gets the catalogue header for a sale type and operation.
 * @param saleType
 * @param isEdit Determines if the catalouge is in create (false) or edit (true) state.
 */
export const getHeader = (saleType: SaleType, isEdit: boolean) => {
  let type = '';

  switch (saleType) {
    case SaleType.PrivateTreatySale:
    case SaleType.SingleVendorAuction:
    default:
      type = 'sales';
      break;
    case SaleType.AllSemenCatalogue:
      type = 'semen';
      break;
  }

  switch (isEdit) {
    case false:
    default:
      return `Create a new ${type} catalogue`;
    case true:
      return `Edit ${type} catalogue`;
  }
};

/**
 * Gets the stepper label for the current step.
 */
export const getStepLabel = (stepNumber: number, isEdit: boolean) => {
  switch (stepNumber) {
    case 1:
      return `${isEdit ? 'Edit catalogue' : 'Create a catalogue'}`;
    case 2:
      return 'Add additional details';
    case 3:
      return 'Select & edit animals';
    default:
      return '';
  }
};

/**
 * Returns the available countries as `DropdownItem`s.
 */
export const getCountries = (): DropdownItem[] => [
  {
    id: 'Australia',
    value: 'Australia',
  },
  {
    id: 'New Zealand',
    value: 'New Zealand',
  },
];

/**
 * Gets the states for the selected country. Defaults to Australia.
 * @param country
 */
export const getStates = (country: string = 'Australia') => {
  switch (country) {
    case 'Australia':
      return [
        {
          id: AustralianState.NewSouthWales,
          value: AustralianState.NewSouthWales,
        },
        {
          id: AustralianState.NorthernTerritory,
          value: AustralianState.NorthernTerritory,
        },
        {
          id: AustralianState.Queensland,
          value: AustralianState.Queensland,
        },
        {
          id: AustralianState.SouthAustralia,
          value: AustralianState.SouthAustralia,
        },
        {
          id: AustralianState.Tasmania,
          value: AustralianState.Tasmania,
        },
        {
          id: AustralianState.Victoria,
          value: AustralianState.Victoria,
        },
        {
          id: AustralianState.WesternAustralia,
          value: AustralianState.WesternAustralia,
        },
      ];
    case 'New Zealand':
      return [
        {
          id: NewZealandState.NewZealand,
          value: NewZealandState.NewZealand,
        },
      ];
    default:
      throw Error('Country is required to get states');
  }
};

/*
 * Creates the `catalougeItems` payload for creating/editing a catalogue.
 * @param catalogueAnimals
 * @param catalogueId
 */
export const getAnimalsPayload = (catalogueAnimals: CatalogueAnimal[], catalogueId: number | undefined) => {
  if (catalogueAnimals && catalogueAnimals.length) {
    const items: CatalogueItem[] = catalogueAnimals.map((animal) => ({
      animalId: animal.animalId,
      displayAnimalId: animal.animalId,
      price: animal.price,
      lotNo: animal.lot ? `${animal.lot}` : null,
      doses: animal.doses,
      catalogueId: `${catalogueId}`,
    }));
    return items;
  }
  return [];
};

/**
 *  Creates animal list from catalogue Items.
 * @param catalogueItems
 */
export const getAnimals = (catalogueItems: CatalogueItem[]) => {
  if (catalogueItems) {
    const animals: CatalogueAnimal[] = catalogueItems.map((item) => ({
      animalId: item.animalId,
      price: item.price,
      lot: Number(item.lotNo),
      sex: item.sex || Sex.Any,
      doses: item.doses,
      displayId: item.displayAnimalId,
    }));

    return animals;
  }

  return [];
};

/**
 * Gets the catalogue payload for creating or editing.
 * @param data The form data
 * @param existingCatalogue The existing catalogue or initial state
 * @param isActive Determines whether this will be saved as a draft (false) or not (true)
 */
export const getCataloguePayload = async (data: Record<string, any>, existingCatalogue: CatalogueExtended | CataloguePayload | undefined, isActive = false) => {
  // Create an empty record to store the catalogue data.
  const payload: Record<string, any> = {};
  const catalogue = existingCatalogue || {};
  Object.entries(catalogue).forEach(([key, value]) => {
    payload[key] = data[key] || value;
  });

  // Set the isActive flag, when publishing this will be true.
  payload.isActive = isActive;

  if (payload.saleType !== SaleType.AllSemenCatalogue) {
    payload.listingEndDate = moment(payload.listingEndDate).startOf('day').format('YYYY-MM-DD HH:mm:SS');
    payload.listingStartDate = moment(payload.listingStartDate).startOf('day').format('YYYY-MM-DD HH:mm:SS');
    payload.saleEndDate = moment(payload.saleEndDate).startOf('day').format('YYYY-MM-DD HH:mm:SS');
    payload.saleStartDate = moment(payload.saleStartDate).startOf('day').format('YYYY-MM-DD HH:mm:SS');
  }

  // Gets the image FormData payload from the attached file
  if (data[CatalogueFormField.Logo] && data[CatalogueFormField.Logo].length) {
    payload.catalogueImages = await getFilePayload(data[CatalogueFormField.Logo][0], catalogue.id);
  } else {
    payload.catalogueImages = catalogue.catalogueImages;
  }

  // Gets the catalogue items (animals)
  if (Object.prototype.hasOwnProperty.call(data, CatalogueFormField.Animals)) {
    payload.catalogueItems = getAnimalsPayload(data[CatalogueFormField.Animals], catalogue.id);
  } else {
    payload.catalogueItems = catalogue.catalogueItems;
  }

  return payload as CataloguePayload;
};

/**

/**
 * Returns true if the `fileType` is a valid template file.
 * @param fileType The file mime type.0
 */
export const isValidAnimalListingType = (fileType: string) => fileType === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';

/**
 * Gets the string for how many animals are being viewed.
 * @param animals
 */
export const getAnimalCount = (animals: CatalogueAnimal[]) => {
  const ramCount = animals.filter((animal) => animal.sex === Sex.Male).length;
  const eweCount = animals.filter((animal) => animal.sex === Sex.Female).length;

  if (ramCount && eweCount) {
    return `Viewing ${ramCount} ${asPlural('ram', ramCount)} & ${eweCount} ${asPlural('ewe', eweCount)}`;
  } if (ramCount && !eweCount) {
    return `Viewing ${ramCount} ${asPlural('ram', ramCount)}`;
  } if (!ramCount && eweCount) {
    return `Viewing ${eweCount} ${asPlural('ewe', eweCount)}`;
  }
};

/**
 * Gets the `CatalogueType` for the provided `SaleType`.
 * @param saleType
 */
export const getCatalogueTypeBySaleType = (saleType: SaleType) => {
  switch (saleType) {
    case SaleType.PrivateTreatySale:
    case SaleType.SingleVendorAuction:
      return CatalogueType.Sales;
    case SaleType.AllSemenCatalogue:
      return CatalogueType.Semen;
    default:
      throw Error('Invalid SaleType');
  }
};

/**
 * Gets a href value to go back to catalogue listings.
 *
 * Strips out any unnecessary create catalogue values such as `type` and `step`.
 * @param query
 */
export const getBackToCataloguesQuerystring = (query: Params) => {
  // Strip out any non required querystring props
  const {
    type,
    step,
    ...rest
  } = query;

  const href = `/catalogue/my${toQuerystring(rest)}`;

  return href;
};

/**
 * Gets the href for the catalogue template.
 * @param saleType
 */
export const getAnimalTemplateUrl = (saleType: SaleType) => {
  const catalogueType = getCatalogueTypeBySaleType(saleType);

  switch (catalogueType) {
    case CatalogueType.Sales:
      return '/Sales catalogue animal list template.xlsx';
    case CatalogueType.Semen:
      return '/Semen catalogue animal list template.xlsx';
    default:
      throw Error('invalid sale type');
  }
};

/**
 * Gets the row number of the animal, excludes the first two example rows.
 * @param rows
 * @param row
 */
const getRowNumber = (rows: CatalogueRowAnimal[], row: CatalogueRowAnimal) => rows.indexOf(row) + 2;

/**
 * Gets the animal id from the XLSX row, this should always be the first property.
 * @param row
 */
const getRowAnimalId = (row: CatalogueRowAnimal) => row['16 digit Animal ID'];

/**
 * Checks a file for duplicate animal ids.
 *
 * Returns an array of errors if any duplicates exist.
 * @param file
 */
export const checkDuplicateAnimalIds = (file: File) => {
  const reader: FileReader = new FileReader();
  return new Promise<ApiError[]>((resolve) => {
    reader.onload = (event: ProgressEvent<FileReader>) => {
      const data = event?.target?.result;
      const book = XLSX.read(data, { type: 'binary' });

      const duplicates: ApiError[] = [];
      const rows: CatalogueRowAnimal[] = XLSX.utils.sheet_to_json(book.Sheets[book.SheetNames[0]]);
      rows.forEach((row) => {
        const id = getRowAnimalId(row);

        // Find any duplicates by comparing the animal id, ignoring itself
        const duplicate = rows.find((r) => getRowAnimalId(r) === id && r !== row);
        if (duplicate) {
          duplicates.push({ errorType: 1, errorMessage: `Row ${getRowNumber(rows, row)}: Animal ID ${id} and Row ${getRowNumber(rows, duplicate)} are duplicates. Please remove one of the duplicates to continue` });
        }
      });

      resolve(duplicates);
    };
    reader.readAsBinaryString(file);
  });
};
